import { Fragment, useEffect, useMemo, useState } from 'react';
import { Loader2, RotateCcw, ShieldCheck, TriangleAlert } from 'lucide-react';
import {
  BOARD_MEMBER_ROLES,
  BOARD_PERMISSIONS,
  OWNER_LOCKED_PERMISSIONS,
  type BoardMemberRole,
  type BoardPermission,
  type BoardRoleMatrix,
} from '@hygge/shared';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  useBoard,
  useBoardRoleMatrix,
  useResetBoardRoleMatrix,
  useSetBoardRoleMatrix,
} from '@/hooks/use-boards';
import { toast } from 'sonner';

const ROLES: BoardMemberRole[] = ['viewer', 'member', 'admin', 'owner'];

const GROUPS = ['Tasks and comments', 'Board setup', 'Ownership'] as const;

/**
 * What each of the four roles may do on THIS board.
 *
 * The role names are fixed and the rows are the board's own. Two things are not
 * on the table and cannot be: editing it, and granting or transferring Owner —
 * a checkbox deciding who may edit the checkboxes would be a lock its holder
 * could turn on themselves. The Owner also always keeps Members, or nobody
 * could ever hand the board on; that cell is shown ticked and disabled.
 */
export function RolePermissionsTab({ projectId }: { projectId: string }) {
  const { data, isLoading } = useBoardRoleMatrix(projectId);
  const { data: board } = useBoard(projectId);
  const save = useSetBoardRoleMatrix(projectId);
  const reset = useResetBoardRoleMatrix(projectId);

  const [draft, setDraft] = useState<BoardRoleMatrix | null>(null);

  useEffect(() => {
    if (data?.matrix) setDraft(data.matrix);
  }, [data?.matrix]);

  const dirty = useMemo(() => {
    if (!draft || !data?.matrix) return false;
    return ROLES.some(
      (role) =>
        draft[role].length !== data.matrix[role].length ||
        draft[role].some((p) => !data.matrix[role].includes(p))
    );
  }, [draft, data?.matrix]);

  // On a public board the Viewer row is everyone in the company who can reach
  // Boards at all — worth saying out loud rather than refusing.
  const publicViewerWarning = board?.visibility === 'public' && (draft?.viewer.length ?? 0) > 0;

  const isLocked = (role: BoardMemberRole, permission: BoardPermission) =>
    role === 'owner' && OWNER_LOCKED_PERMISSIONS.includes(permission);

  const toggle = (role: BoardMemberRole, permission: BoardPermission) => {
    if (isLocked(role, permission)) return;
    setDraft((prev) => {
      if (!prev) return prev;
      const held = prev[role].includes(permission);
      return {
        ...prev,
        [role]: held ? prev[role].filter((p) => p !== permission) : [...prev[role], permission],
      };
    });
  };

  if (isLoading || !draft) {
    return (
      <div className="flex items-center justify-center py-12 text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
      </div>
    );
  }

  return (
    <TooltipProvider delayDuration={200}>
      <div className="space-y-5">
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-1">
            <p className="text-[13px] text-muted-foreground">
              What each role may do on this board. The names are fixed; everything else is yours to
              set, and this board may well give its Member more than its Admin.
            </p>
            <p className="text-[12px] text-muted-foreground">
              {data?.customized
                ? 'This board has its own table.'
                : 'This board follows the product defaults, so a change to those defaults reaches it automatically — until you save here.'}
            </p>
          </div>
          {data?.customized && (
            <Button
              variant="ghost"
              size="sm"
              className="shrink-0 gap-1.5 text-[12px]"
              disabled={reset.isPending}
              onClick={() =>
                reset.mutate(undefined, {
                  onSuccess: () => toast.success('Following the defaults again'),
                  onError: () => toast.error('Could not reset the permissions'),
                })
              }
            >
              <RotateCcw className="h-3.5 w-3.5" />
              Follow the defaults
            </Button>
          )}
        </div>

        {publicViewerWarning && (
          <div className="flex gap-2.5 rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2.5 text-[12px]">
            <TriangleAlert className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600" />
            <span>
              This board is public, so its Viewer row is everyone in the company who can open
              Boards. Anything ticked there, they can all do.
            </span>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="border-b">
                <th className="py-2 pr-4 text-left font-medium text-muted-foreground">Action</th>
                {ROLES.map((role) => (
                  <th key={role} className="w-24 px-2 py-2 text-center font-medium">
                    {BOARD_MEMBER_ROLES.find((r) => r.value === role)?.label ?? role}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {GROUPS.map((group) => {
                const rows = BOARD_PERMISSIONS.filter((p) => p.group === group);
                if (rows.length === 0) return null;
                return (
                  <Fragment key={group}>
                    <tr>
                      <td
                        colSpan={ROLES.length + 1}
                        className="pt-5 pb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground"
                      >
                        {group}
                      </td>
                    </tr>
                    {rows.map((row) => (
                      <tr key={row.value} className="border-b border-border/50">
                        <td className="py-2.5 pr-4">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="cursor-help border-b border-dotted border-muted-foreground/40">
                                {row.label}
                              </span>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs text-[12px]">
                              {row.description}
                            </TooltipContent>
                          </Tooltip>
                        </td>
                        {ROLES.map((role) => {
                          const locked = isLocked(role, row.value);
                          const cell = (
                            <Checkbox
                              checked={draft[role].includes(row.value)}
                              disabled={locked}
                              onCheckedChange={() => toggle(role, row.value)}
                              aria-label={`${row.label} for ${role}`}
                            />
                          );
                          return (
                            <td key={role} className="px-2 py-2.5 text-center">
                              <div className="flex justify-center">
                                {locked ? (
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <span className="inline-flex">{cell}</span>
                                    </TooltipTrigger>
                                    <TooltipContent className="max-w-xs text-[12px]">
                                      The Owner always keeps this. Without it nobody could hand the
                                      board on.
                                    </TooltipContent>
                                  </Tooltip>
                                ) : (
                                  cell
                                )}
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between gap-3 border-t pt-4">
          <span className="flex items-center gap-1.5 text-[12px] text-muted-foreground">
            <ShieldCheck className="h-3.5 w-3.5" />
            Reading the board, editing this table and handing over ownership are not on it — they
            follow being the Owner.
          </span>
          <div className="flex gap-2">
            <Button
              variant="ghost"
              size="sm"
              disabled={!dirty || save.isPending}
              onClick={() => data?.matrix && setDraft(data.matrix)}
            >
              Discard
            </Button>
            <Button
              size="sm"
              disabled={!dirty || save.isPending}
              onClick={() =>
                save.mutate(draft, {
                  onSuccess: () => toast.success('Board permissions saved'),
                  onError: () => toast.error('Could not save the permissions'),
                })
              }
            >
              {save.isPending && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
              Save
            </Button>
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
}
